<template>
  <div
    class="BaseTemplateMainSection relative-position"
    :class="{
      'BaseTemplateMainSection--active': !templateStore.isDigitalMenu && !hasPrinter && isLayerActiveRef,
      'BaseTemplateMainSection--moving': isLayerMovingRef,
      'BaseTemplateMainSection--printer': hasPrinter,
      'BaseTemplateMainSection--digital-menu': templateStore.isDigitalMenu,
    }"
    :style="backgroundStyle"
  >
    <div
      :id="layer.id"
      :style="`height: ${layer.height}px`"
      class="overflow-hidden relative-position"
      @click="
        templateStore.handleActiveLayer({
          index: layerIndex,
          id: layer.id,
          updatedFrom: 'section',
        })
      "
      @click.self="templateStore.handleActiveItem({})"
    >
      <div
        v-for="(item, itemIndex) in layer.items"
        :key="item.id"
        class="absolute flex BaseTemplateMainSection__item-wrapper"
        :class="{
          hidden: !item.is_show,
          transition: !templateStore.isDragging,
          'BaseTemplateMainSection__item-wrapper--grab': !hasPrinter,
        }"
        :style="`top: ${item.start_position.y}px; left: ${item.start_position.x}px; z-index: ${
          980 - itemIndex
        }; transform: rotate(${item.rotate}deg)`"
        @mousedown="(e) => onItemMouseDown(e, item)"
        @dragstart.prevent
        @click.capture="(e) => onItemCaptureClick(e, item)"
      >
        <BaseTemplateContentText
          v-if="item.type === 'text'"
          class="BaseTemplateMainSection__item-content"
          :class="{
            'BaseTemplateMainSection__item-content--active': isItemActive(item),
            'BaseTemplateMainSection__item-content--printer': hasPrinter,
            'BaseTemplateMainSection__item-content--linked': item.is_field && !hasPrinter,
          }"
          :item="item"
          :item-index="itemIndex"
          :layer-index="layerIndex"
          :has-printer="hasPrinter"
        />
        <span
          v-if="item.type === 'image'"
          class="BaseTemplateMainSection__item-content flex BaseTemplateMainSection__item-shape-image-content transition"
          :class="{
            'BaseTemplateMainSection__item-content--active BaseTemplateMainSection__item-shape-image-content--active':
              isItemActive(item),
            'BaseTemplateMainSection__item-shape-image-content--printer': hasPrinter,
          }"
          @click="templateStore.handleActiveItem(item)"
        >
          <img
            class="BaseTemplateMainSection__image"
            :src="
              templateStore.newUploadImages[item.id]?.blob ??
              `${templateStore.layers[layerIndex].items[itemIndex].value}?t=${currentDate}`
            "
            :style="[`width: ${item.width}px; height: ${item.height}px; border-radius: ${item.radius}px`]"
            :class="{ 'BaseTemplateMainSection__image-mono-color': templateStore.isPrintTemplate }"
            @error="$event.target.src = addImage"
          />
        </span>
        <BaseTemplateContentShape
          v-if="item.type === 'shape'"
          class="BaseTemplateMainSection__item-content BaseTemplateMainSection__item-shape-image-content"
          :class="{
            'BaseTemplateMainSection__item-content--active BaseTemplateMainSection__item-shape-image-content--active':
              isItemActive(item),
            'BaseTemplateMainSection__item-shape-image-content--printer': hasPrinter,
          }"
          :item="item"
        />

        <template v-if="!hasPrinter && isItemActive(item) && !templateStore.isMultiSelect">
          <span
            v-for="direction in item.type === 'text' ? TEXT_RESIZE_DIRECTIONS : RESIZE_DIRECTIONS"
            :key="direction"
            class="BaseTemplateMainSection__handle"
            :class="`BaseTemplateMainSection__handle--${direction}`"
            :style="{ cursor: handleCursor(direction, item.rotate) }"
            @mousedown.stop="(e) => handleItemResizeStart(item, direction, e)"
          />
        </template>
      </div>
    </div>
    <span
      v-if="!hasPrinter && !templateStore.isDigitalMenu"
      class="BaseTemplateMainSection__icon transition flex"
      @mousedown="handleResizeStart"
    >
      <k-icon name="draggable" size="30px" />
      <k-tooltip label="Extend" position="top" />
    </span>
  </div>
</template>

<script setup>
import useBaseTemplateMainSectionHook from './BaseTemplateMainSectionHook'
import BaseTemplateContentShape from './BaseTemplateContentShape'
import BaseTemplateContentText from './BaseTemplateContentText'
import addImage from 'src/assets/add-image.svg'

const RESIZE_DIRECTIONS = ['nw', 'ne', 'sw', 'se']
// Text height follows its wrapped lines, so text only gets side handles for its width
const TEXT_RESIZE_DIRECTIONS = ['w', 'e']
// Angle each handle points to on an unrotated item, clockwise from the right
const HANDLE_ANGLES = { e: 0, se: 45, s: 90, sw: 135, w: 180, nw: 225, n: 270, ne: 315 }
const RESIZE_CURSORS = ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize']

// Handles rotate with the item, so the cursor follows where the handle points on screen
function handleCursor(direction, rotation = 0) {
  const angle = (((HANDLE_ANGLES[direction] + rotation) % 180) + 180) % 180
  return RESIZE_CURSORS[Math.round(angle / 45) % 4]
}

const props = defineProps({
  layerIndex: { type: Number, required: true },
  layer: { type: Object, required: true },
  hasPrinter: { type: Boolean, default: false },
})

const {
  templateStore,
  currentDate,
  isLayerMovingRef,
  isLayerActiveRef,
  backgroundStyle,
  handleResizeStart,
  handleItemMoveStart,
  handleItemResizeStart,
} = useBaseTemplateMainSectionHook(props)

// The printed receipt (and its preview) never shows the editor's selection
function isItemActive(item) {
  return !props.hasPrinter && templateStore.selectedItemIds.includes(item.id)
}

function onItemCaptureClick(event, item) {
  if (!event.shiftKey && !templateStore.isMultiSelectMode) return
  if (event.target?.closest?.('.BaseTemplateMainSection__handle')) return
  event.preventDefault()
  event.stopPropagation()
  templateStore.toggleItemSelection(item.id)
}

// Native drag-and-drop (images, selected text) is blocked with @dragstart.prevent on the item wrapper:
// it swallows mousemove/mouseup, so the item would keep following the cursor until the next click.
function onItemMouseDown(event, item) {
  if (event.button !== 0) return
  // Let resize handles handle their own mousedown
  if (event.target?.closest?.('.BaseTemplateMainSection__handle')) return
  if (event.shiftKey || templateStore.isMultiSelectMode) {
    // The click that follows toggles this item in the selection (onItemCaptureClick), so mousedown must
    // not replace the selection or put a caret in a text. Only an item already selected drags the selection.
    event.preventDefault()
    document.activeElement?.blur?.()
    if (!isItemActive(item)) return
  }
  // Texts are tracked even while being edited: a click still places the caret, and a drag past the
  // move threshold blurs the textarea and moves the item instead of selecting text
  handleItemMoveStart(item, event)
}
</script>

<style lang="sass" scoped>
@import 'src/shared/styles/variables.sass'
@import 'src/shared/styles/mixins.sass'

%section-extend-icon-active
  opacity: 1
  transform: translate(50%, 50%) rotate(0)
  pointer-events: all
  cursor: grab
  z-index: 981
  background-color: rgba(167, 211, 235, 0.20)
  border-radius: 100%

.BaseTemplateMainSection
  transition: 0.3s ease background
  background-image: none
  &:hover::before
    content: ''
    position: absolute
    z-index: 981
    width: 100%
    height: 100%
    top: 0
    left: 0
    pointer-events: none
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%231B72A4' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")
  &:hover
    background-color: rgba(167, 211, 235, 0.20)
    .BaseTemplateMainSection__icon
      @extend %section-extend-icon-active
  @include state-modifier('active')
    background-color: rgba(167, 211, 235, 0.20)
    &::before
      content: ''
      position: absolute
      z-index: 981
      width: 100%
      height: 100%
      top: 0
      left: 0
      pointer-events: none
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%231B72A4' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")
    .BaseTemplateMainSection__icon
      @extend %section-extend-icon-active

  @include state-modifier('printer')
    background-image: none
    &::before
      content: none !important

  @include state-modifier('digital-menu')
    &:hover
      background-color: transparent
      &::before
        content: none

  @include element('item-wrapper')
    user-select: none
    @include state-modifier('grab')
      cursor: grab
      &:active
        cursor: grabbing

  @include element('icon')
    opacity: 0
    z-index: 981
    pointer-events: none
    position: absolute
    bottom: 0
    right: 0
    transform: translate(50%, 50%) rotate(180deg)
    user-select: none
    &:hover
      @extend %section-extend-icon-active

  @include element('item-content')
    border: none
    background-color: var(--transparent)
    outline: none
    &:hover, &:focus, &:active, &:focus-within
      border: none
      outline: none
      background-color: rgba(255, 255, 255, 0.5)
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2300A8DD' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")

    @include state-modifier('active')
      border: none
      outline: none
      background-color: rgba(255, 255, 255, 0.5)
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2300A8DD' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")

    @include state-modifier('linked')
      text-decoration: underline
      text-decoration-color: var(--info-70)
      text-decoration-thickness: 1.5px
      text-underline-offset: 2px

    @include state-modifier('printer')
      background-image: none
      border: none
      &:hover, &:focus, &:active, &:focus-within
        background-image: none

  @include element('item-shape-image-content')
    padding: 2px
    box-sizing: content-box
    object-fit: scale-down
    object-position: center
    background-image: none
    border: 2px dashed transparent

    &:hover, &:focus, &:active, &:focus-within
      background-image: none
      border: 2px dashed #00A8DD

    @include state-modifier('active')
      background-image: none
      border: 2px dashed #00A8DD

    @include state-modifier('printer')
      background-image: none
      border: none
      &:hover, &:focus, &:active, &:focus-within
        background-image: none
        border: none

  @include element('image')
    object-fit: cover
  @include element('image-mono-color')
    filter: grayscale(100%) contrast(10000%) brightness(150%)

  @include element('handle')
    position: absolute
    width: 10px
    height: 10px
    background: white
    border: 2px solid #00A8DD
    border-radius: 2px
    z-index: 990
    pointer-events: all
    // The cursor is set inline from the item's rotation (handleCursor)
    &--nw
      top: -5px
      left: -5px
    &--ne
      top: -5px
      right: -5px
    &--sw
      bottom: -5px
      left: -5px
    &--se
      bottom: -5px
      right: -5px
    &--w
      top: 50%
      left: -5px
      transform: translateY(-50%)
    &--e
      top: 50%
      right: -5px
      transform: translateY(-50%)
</style>

<style lang="sass">
/* FIXME @Kasra we cant use :global for this style, it doesnt work */
.BaseTemplateMain:has(.BaseTemplateMainSection--moving)
  .BaseTemplateMainSection:not(.BaseTemplateMainSection--moving)
    pointer-events: none
</style>
